import { forwardRef, useCallback, useMemo, useState } from "react";
import { NavLink } from "react-router";
import classNames from "classnames";

import { useButtonProps } from "./useButtonProps";
import { useRefocusOnNextRender } from "../Utils/useRefocusOnNextRender";
import { assignForwardedRef } from "../Utils/assignForwardedRef";
import KEY_CODES from "../Utils/keyCodes";

const ButtonLink = ({ LinkComponent = NavLink, ...props }, ref) => {
  const [linkNode, setLinkNode] = useState();

  const {
    passthroughProps: inheritedPassthroughProps,
    className: inheritedClassName,
    content
  } = useButtonProps(props);

  const refocusOnNextRender = useRefocusOnNextRender(linkNode);

  const { disabled } = inheritedPassthroughProps;
  const className = classNames(inheritedClassName, { disabled });

  const passthroughProps = useMemo(() => {
    const newProps = { ...inheritedPassthroughProps };

    if (!newProps.href && newProps.to) newProps.href = newProps.to;

    delete newProps.to;

    if (!newProps.tabIndex && disabled) newProps.tabIndex = "-1";

    return newProps;
  }, [disabled, inheritedPassthroughProps]);

  const linkNodeRef = useCallback(
    node => {
      // Keep a stable ref callback to avoid ref churn that can cause state update loops.
      // Only update state when the ref actually changes, and keep the node for focus
      // restoration / keyboard activation.
      setLinkNode(prev => (prev === node ? prev : node));

      // If a ref is provided, call it so it's set for the parent
      assignForwardedRef(ref, node);
    },
    [ref]
  );

  const handleClick = useCallback(
    e => {
      // Ensure that the link will be focused back after the navigation is done and the
      // component is re-rendered
      refocusOnNextRender();

      if (passthroughProps.onClick) passthroughProps.onClick(e);

      // Prevent NavLink to handle the click event, and let the `<ExternalLinksManager>`
      // handle it instead, to support external links and library file links.
      if (LinkComponent === NavLink) e.preventDefault();
    },
    [LinkComponent, passthroughProps, refocusOnNextRender]
  );

  const handleKeyDown = useCallback(
    e => {
      // Those two keys actually navigate to the target of the link, so we ensure that
      // the link will be focused back after the navigation is done and the component is
      // re-rendered
      if (e.keyCode === KEY_CODES.SPACE || e.keyCode === KEY_CODES.ENTER) {
        refocusOnNextRender();
      }

      // Simulate the click event when the space bar is used, as it's required by ARIA
      // guidelines for a button link. Also avoid the page to scroll down as it's the
      // default browser action when Space is pressed and the focus is on a link.
      if (linkNode && e.keyCode === KEY_CODES.SPACE) {
        e.preventDefault();

        linkNode.click();
      }
    },
    [linkNode, refocusOnNextRender]
  );

  if (passthroughProps.href && LinkComponent === NavLink) {
    passthroughProps.to = passthroughProps.href;
    delete passthroughProps.href;
  }

  return (
    <LinkComponent
      {...passthroughProps}
      ref={linkNodeRef}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
    >
      {content}
    </LinkComponent>
  );
};

export default forwardRef(ButtonLink);
